import { PerlinNoise } from '@shared/utils/PerlinNoise';
import { TileType, BiomeType } from './IsometricMap';

export interface MapFeature {
  type: 'tree' | 'rock' | 'bush' | 'house' | 'tent' | 'campfire' | 'bridge' | 'well' | 'cart' | 'barrel' | 'crate' | 'torch' | 'sign' | 'grave' | 'ruins' | 'statue';
  x: number;
  y: number;
  variant?: number;
}

export interface GeneratedMap {
  tiles: TileType[][];
  features: MapFeature[];
  riverTiles: Set<string>;
  bridgeTiles: Set<number>;
  pathTiles: Set<string>;
  heightmap: number[][];
}

export class MapGenerator {
  private width: number;
  private height: number;
  private noise: PerlinNoise;
  private tiles: TileType[][];
  private features: MapFeature[] = [];
  private riverTiles: Set<string> = new Set();
  private bridgeTiles: Set<number> = new Set();
  private pathTiles: Set<string> = new Set();
  private biome: BiomeType;
  private heightmap: number[][] = [];

  constructor(width: number, height: number, biome: BiomeType, seed: number) {
    this.width = width;
    this.height = height;
    this.biome = biome;
    this.noise = new PerlinNoise(seed);
    this.tiles = [];

    // Initialize with primary tile
    const primaryTile = this.getPrimaryTile();
    for (let y = 0; y < height; y++) {
      this.tiles[y] = [];
      for (let x = 0; x < width; x++) {
        this.tiles[y][x] = primaryTile;
      }
    }
  }

  generate(): GeneratedMap {
    // 1. Generate heightmap
    this.generateHeightmap();

    // 2. Create natural terrain variation
    this.generateBaseTerrain();

    // 3. Carve a river (if not desert/hell)
    if (this.biome !== 'desert' && this.biome !== 'hell') {
      this.generateRiver();
    }

    // 4. Create main road from spawn to edges
    this.generateMainRoads();

    // 5. Add bridges over river
    this.generateBridges();

    // 6. Apply terrain features (mountains, hills)
    this.generateTerrain();

    // 7. Add water features (ponds)
    this.generateWaterFeatures();

    // 8. Create forest areas
    this.generateForests();

    // 9. Create clearings with structures
    this.generateClearings();

    // 10. Add natural border (cliffs/dense trees)
    this.generateBorder();

    // 11. Place zone-appropriate decorations
    this.generateDecorations();

    // 12. Sculpt heightmap for rivers/paths (depress valleys, flatten roads)
    this.sculptHeightmapForFeatures();

    return {
      tiles: this.tiles,
      features: this.features,
      riverTiles: this.riverTiles,
      bridgeTiles: this.bridgeTiles,
      pathTiles: this.pathTiles,
      heightmap: this.heightmap,
    };
  }

  private getPrimaryTile(): TileType {
    switch (this.biome) {
      case 'forest': return TileType.GRASS;
      case 'swamp': return TileType.DARK_GRASS;
      case 'desert': return TileType.SAND;
      case 'mountains': return TileType.STONE;
      case 'underworld': return TileType.VOID;
      case 'sacred': return TileType.GRASS;
      case 'hell': return TileType.VOID;
      default: return TileType.GRASS;
    }
  }

  private generateBaseTerrain(): void {
    const primary = this.getPrimaryTile();
    const secondary = this.getSecondaryTile();

    // Biome-specific noise frequency and threshold
    let freq = 0.08;
    let threshold = 0.3;
    switch (this.biome) {
      case 'forest':
        freq = 0.06; threshold = 0.25; // More dark grass patches
        break;
      case 'swamp':
        freq = 0.1; threshold = 0.15; // Lots of mud variation
        break;
      case 'desert':
        freq = 0.05; threshold = 0.35; // Gentle dune variation
        break;
      case 'mountains':
        freq = 0.07; threshold = 0.2; // Rocky variation
        break;
      case 'underworld':
        freq = 0.09; threshold = 0.2; // Stone patches in void
        break;
    }

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const n = this.noise.fbm(x * freq, y * freq, 2, 2, 0.5);

        if (n > threshold) {
          this.tiles[y][x] = secondary;
        } else {
          this.tiles[y][x] = primary;
        }
      }
    }
  }

  private getSecondaryTile(): TileType {
    switch (this.biome) {
      case 'forest': return TileType.DARK_GRASS;
      case 'swamp': return TileType.MUD;
      case 'desert': return TileType.DIRT;
      case 'mountains': return TileType.DIRT;
      case 'underworld': return TileType.STONE;
      case 'sacred': return TileType.SAND;
      case 'hell': return TileType.STONE;
      default: return TileType.DIRT;
    }
  }

  private generateHeightmap(): void {
    this.heightmap = [];

    // Biome-specific heightmap parameters
    let baseFreq: number, detailFreq: number, detailAmp: number;
    let microFreq: number, microAmp: number;
    let heightScale: number, heightOffset: number;

    switch (this.biome) {
      case 'mountains':
        baseFreq = 0.035; detailFreq = 0.09; detailAmp = 0.4;
        microFreq = 0.25; microAmp = 0.08;
        heightScale = 1.3; heightOffset = 0.1;
        break;
      case 'swamp':
        baseFreq = 0.05; detailFreq = 0.12; detailAmp = 0.15;
        microFreq = 0.3; microAmp = 0.04;
        heightScale = 0.35; heightOffset = 0.25;
        break;
      case 'desert':
        baseFreq = 0.03; detailFreq = 0.15; detailAmp = 0.25;
        microFreq = 0.4; microAmp = 0.06;
        heightScale = 0.6; heightOffset = 0.3;
        break;
      case 'hell':
      case 'underworld':
        baseFreq = 0.04; detailFreq = 0.1; detailAmp = 0.35;
        microFreq = 0.2; microAmp = 0.1;
        heightScale = 0.8; heightOffset = 0.15;
        break;
      default: // forest, sacred
        baseFreq = 0.04; detailFreq = 0.1; detailAmp = 0.3;
        microFreq = 0.22; microAmp = 0.05;
        heightScale = 1.0; heightOffset = 0.0;
        break;
    }

    for (let y = 0; y < this.height; y++) {
      this.heightmap[y] = [];
      for (let x = 0; x < this.width; x++) {
        // Multi-octave noise for natural terrain
        let h = this.noise.fbm(x * baseFreq, y * baseFreq, 3, 2, 0.5);
        h += this.noise.fbm(x * detailFreq, y * detailFreq, 2, 2, 0.5) * detailAmp;
        // Micro detail: small bumps and dips for organic feel
        h += this.noise.fbm(x * microFreq, y * microFreq, 2, 2, 0.5) * microAmp;

        // Apply biome scaling
        h = (h + 0.5) * heightScale + heightOffset;

        // Clamp to 0-1 range
        this.heightmap[y][x] = Math.max(0, Math.min(1, h));
      }
    }
  }

  /** Depress heightmap under rivers and paths so they sit in natural valleys */
  private sculptHeightmapForFeatures(): void {
    // Depress river tiles (deeper in center)
    for (const key of this.riverTiles) {
      const [x, y] = key.split(',').map(Number);
      if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
        this.heightmap[y][x] = Math.min(this.heightmap[y][x], 0.25);
      }
      // Carve surrounding area for smooth transition (valley walls)
      for (let dy = -3; dy <= 3; dy++) {
        for (let dx = -3; dx <= 3; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && nx < this.width && ny >= 0 && ny < this.height) {
            if (this.riverTiles.has(`${nx},${ny}`)) continue;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const falloff = Math.max(0, 1 - dist / 3.5); // smooth falloff
            const depression = falloff * 0.15;
            this.heightmap[ny][nx] = Math.max(0.2, this.heightmap[ny][nx] - depression);
          }
        }
      }
    }

    // Slightly flatten path tiles (roads are carved into terrain)
    for (const key of this.pathTiles) {
      const [x, y] = key.split(',').map(Number);
      if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
        // Blend toward a moderate height
        this.heightmap[y][x] = this.heightmap[y][x] * 0.6 + 0.38 * 0.4;
      }
    }
  }

  private generateTerrain(): void {
    if (this.biome === 'underworld' || this.biome === 'hell') return; // No terrain for dungeons

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const h = this.heightmap[y]?.[x] ?? 0.5;

        // Skip if already water or path
        if (this.riverTiles.has(`${x},${y}`)) continue;
        if (this.pathTiles.has(`${x},${y}`)) continue;

        // Height thresholds for terrain types
        if (h > 0.82) {
          this.tiles[y][x] = TileType.MOUNTAIN;
        } else if (h > 0.70) {
          this.tiles[y][x] = TileType.HILL;
        } else if (h > 0.65 && this.biome === 'mountains') {
          this.tiles[y][x] = TileType.ROCKY_GROUND;
        }

        // Add cliff edges around mountains
        if (h > 0.82) {
          // Check adjacent tiles for elevation difference
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue;
              const nx = x + dx;
              const ny = y + dy;
              if (nx >= 0 && nx < this.width && ny >= 0 && ny < this.height) {
                const nh = this.heightmap[ny]?.[nx] ?? 0.5;
                if (nh < 0.70 && this.tiles[ny][nx] !== TileType.WATER) {
                  this.tiles[ny][nx] = TileType.CLIFF_EDGE;
                }
              }
            }
          }
        }
      }
    }
  }

  private generateWaterFeatures(): void {
    if (this.biome === 'desert' || this.biome === 'hell') return;

    const numPonds = 1 + Math.floor(Math.random() * 2);

    for (let p = 0; p < numPonds; p++) {
      const px = 10 + Math.random() * (this.width - 20);
      const py = 10 + Math.random() * (this.height - 20);

      // Don't place near center or paths
      const dcx = px - this.width / 2;
      const dcy = py - this.height / 2;
      const distToCenter = Math.sqrt(dcx * dcx + dcy * dcy);
      if (distToCenter < 12) continue;

      const radius = 2 + Math.random() * 3;

      for (let y = py - radius - 1; y <= py + radius + 1; y++) {
        for (let x = px - radius - 1; x <= px + radius + 1; x++) {
          const ix = Math.floor(x);
          const iy = Math.floor(y);
          if (ix < 0 || ix >= this.width || iy < 0 || iy >= this.height) continue;
          if (this.pathTiles.has(`${ix},${iy}`)) continue;

          const dist = Math.sqrt((x - px) ** 2 + (y - py) ** 2);

          if (dist <= radius * 0.5) {
            this.tiles[iy][ix] = this.biome === 'swamp' ? TileType.SWAMP_WATER : TileType.DEEP_WATER;
            this.riverTiles.add(`${ix},${iy}`);
          } else if (dist <= radius) {
            this.tiles[iy][ix] = this.biome === 'swamp' ? TileType.SWAMP_WATER : TileType.SHALLOW_WATER;
            this.riverTiles.add(`${ix},${iy}`);
          }
        }
      }
    }
  }

  private generateRiver(): void {
    // River flows from one side to another
    const startSide = Math.floor(Math.random() * 2); // 0 = top, 1 = left

    let x: number, y: number;

    if (startSide === 0) {
      // Start from top, flow to bottom
      x = this.width * 0.3 + Math.random() * this.width * 0.4;
      y = 0;
    } else {
      // Start from left, flow to right
      x = 0;
      y = this.height * 0.3 + Math.random() * this.height * 0.4;
    }

    const riverWidth = 2 + Math.floor(Math.random() * 2);

    // Maximum iterations to prevent infinite loop
    const maxIterations = this.width + this.height;
    let iterations = 0;

    while (iterations < maxIterations) {
      iterations++;

      // Carve river
      for (let dy = -riverWidth; dy <= riverWidth; dy++) {
        for (let dx = -riverWidth; dx <= riverWidth; dx++) {
          const rx = Math.floor(x + dx);
          const ry = Math.floor(y + dy);
          if (rx >= 0 && rx < this.width && ry >= 0 && ry < this.height) {
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist <= riverWidth) {
              if (this.biome === 'swamp') {
                // Use swamp water variant
                if (dist <= riverWidth * 0.5) {
                  this.tiles[ry][rx] = TileType.WATER;
                } else {
                  this.tiles[ry][rx] = TileType.SWAMP_WATER;
                }
              } else {
                if (dist <= riverWidth * 0.5) {
                  this.tiles[ry][rx] = TileType.DEEP_WATER;
                } else if (dist <= riverWidth * 0.8) {
                  this.tiles[ry][rx] = TileType.WATER;
                } else {
                  this.tiles[ry][rx] = TileType.SHALLOW_WATER;
                }
              }
              this.riverTiles.add(`${rx},${ry}`);
            }
          }
        }
      }

      // Meander
      const wander = this.noise.noise2D(x * 0.1, y * 0.1) * 0.8;

      if (startSide === 0) {
        x += wander * 2;
        y += 1;
        // Stop when we reach bottom
        if (y >= this.height - riverWidth - 1) break;
      } else {
        x += 1;
        y += wander * 2;
        // Stop when we reach right edge
        if (x >= this.width - riverWidth - 1) break;
      }

      // Stay in bounds
      x = Math.max(riverWidth, Math.min(this.width - riverWidth - 1, x));
      y = Math.max(riverWidth, Math.min(this.height - riverWidth - 1, y));
    }
  }

  private generateMainRoads(): void {
    const centerX = Math.floor(this.width / 2);
    const centerY = Math.floor(this.height / 2);

    // Create roads from center to 4 directions
    const roadWidth = 2;

    // Road to top
    this.carvePath(centerX, centerY, centerX + (Math.random() - 0.5) * 10, 5, roadWidth);
    // Road to bottom
    this.carvePath(centerX, centerY, centerX + (Math.random() - 0.5) * 10, this.height - 5, roadWidth);
    // Road to left
    this.carvePath(centerX, centerY, 5, centerY + (Math.random() - 0.5) * 10, roadWidth);
    // Road to right
    this.carvePath(centerX, centerY, this.width - 5, centerY + (Math.random() - 0.5) * 10, roadWidth);
  }

  private carvePath(x1: number, y1: number, x2: number, y2: number, width: number): void {
    let x = x1;
    let y = y1;
    const steps = Math.max(Math.abs(x2 - x1), Math.abs(y2 - y1)) * 2;

    for (let i = 0; i < steps; i++) {
      // Carve path tiles
      for (let dy = -width; dy <= width; dy++) {
        for (let dx = -width; dx <= width; dx++) {
          const px = Math.floor(x + dx);
          const py = Math.floor(y + dy);
          if (px >= 0 && px < this.width && py >= 0 && py < this.height) {
            // Don't overwrite river
            if (!this.riverTiles.has(`${px},${py}`)) {
              this.tiles[py][px] = TileType.DIRT;
              this.pathTiles.add(`${px},${py}`);
            }
          }
        }
      }

      // Move towards target with some noise
      const dx = x2 - x;
      const dy = y2 - y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 1) break;

      const wander = this.noise.noise2D(x * 0.2, y * 0.2) * 0.3;
      x += (dx / dist) + wander;
      y += (dy / dist) + wander;
    }
  }

  private generateBridges(): void {
    // Find where paths cross rivers
    for (const pathKey of this.pathTiles) {
      const [px, py] = pathKey.split(',').map(Number);

      // Check if adjacent to river
      let foundRiver = false;
      for (let dy = -1; dy <= 1 && !foundRiver; dy++) {
        for (let dx = -1; dx <= 1 && !foundRiver; dx++) {
          const nx = px + dx;
          const ny = py + dy;
          if (this.riverTiles.has(`${nx},${ny}`)) {
            // Create bridge here
            this.createBridge(px, py);
            foundRiver = true;
          }
        }
      }
    }
  }

  private createBridge(x: number, y: number): void {
    // Determine bridge direction based on river flow
    const isVerticalRiver = this.riverTiles.has(`${x},${y - 3}`) || this.riverTiles.has(`${x},${y + 3}`);

    const bridgeLength = 6;
    const bridgeWidth = 2;

    if (isVerticalRiver) {
      // Horizontal bridge
      for (let dx = -bridgeLength / 2; dx <= bridgeLength / 2; dx++) {
        for (let dy = -bridgeWidth / 2; dy <= bridgeWidth / 2; dy++) {
          const bx = Math.floor(x + dx);
          const by = Math.floor(y + dy);
          if (bx >= 0 && bx < this.width && by >= 0 && by < this.height) {
            this.tiles[by][bx] = TileType.STONE;
            this.bridgeTiles.add(bx * 10000 + by);
            this.riverTiles.delete(`${bx},${by}`);
          }
        }
      }
    } else {
      // Vertical bridge
      for (let dy = -bridgeLength / 2; dy <= bridgeLength / 2; dy++) {
        for (let dx = -bridgeWidth / 2; dx <= bridgeWidth / 2; dx++) {
          const bx = Math.floor(x + dx);
          const by = Math.floor(y + dy);
          if (bx >= 0 && bx < this.width && by >= 0 && by < this.height) {
            this.tiles[by][bx] = TileType.STONE;
            this.bridgeTiles.add(bx * 10000 + by);
            this.riverTiles.delete(`${bx},${by}`);
          }
        }
      }
    }

    // Add bridge feature marker
    this.features.push({ type: 'bridge', x, y });
  }

  private generateForests(): void {
    if (this.biome === 'desert' || this.biome === 'hell' || this.biome === 'underworld') {
      return; // No forests in these biomes
    }

    // Create forest clusters
    const numForests = 3 + Math.floor(Math.random() * 3);

    for (let f = 0; f < numForests; f++) {
      const fx = 10 + Math.random() * (this.width - 20);
      const fy = 10 + Math.random() * (this.height - 20);
      const radius = 8 + Math.random() * 8;

      // Don't place forest at center (spawn area)
      const dcx = fx - this.width / 2;
      const dcy = fy - this.height / 2;
      const distToCenter = Math.sqrt(dcx * dcx + dcy * dcy);
      if (distToCenter < 12) continue;

      // Place trees in forest area
      for (let y = fy - radius; y < fy + radius; y++) {
        for (let x = fx - radius; x < fx + radius; x++) {
          const ddx = x - fx;
          const ddy = y - fy;
          const dist = Math.sqrt(ddx * ddx + ddy * ddy);
          const noise = this.noise.noise2D(x * 0.3, y * 0.3);

          if (dist < radius + noise * 3) {
            const ix = Math.floor(x);
            const iy = Math.floor(y);

            if (ix < 0 || ix >= this.width || iy < 0 || iy >= this.height) continue;
            if (this.riverTiles.has(`${ix},${iy}`)) continue;
            if (this.pathTiles.has(`${ix},${iy}`)) continue;
            if (this.tiles[iy]?.[ix] === TileType.MOUNTAIN || this.tiles[iy]?.[ix] === TileType.HILL) continue;

            // Chance to place tree
            if (Math.random() < 0.15) {
              const treeType: MapFeature['type'] = 'tree';
              this.features.push({
                type: treeType,
                x: ix,
                y: iy,
                variant: Math.floor(Math.random() * 3)
              });
            }
          }
        }
      }
    }
  }

  private generateClearings(): void {
    // Create 1-2 clearings with structures
    const numClearings = 1 + Math.floor(Math.random() * 2);

    for (let c = 0; c < numClearings; c++) {
      // Find a spot away from center and paths
      let cx: number, cy: number;
      let attempts = 0;

      do {
        cx = 12 + Math.random() * (this.width - 24);
        cy = 12 + Math.random() * (this.height - 24);
        attempts++;
      } while (
        attempts < 20 && (
          Math.sqrt((cx - this.width / 2) * (cx - this.width / 2) + (cy - this.height / 2) * (cy - this.height / 2)) < 15 ||
          this.riverTiles.has(`${Math.floor(cx)},${Math.floor(cy)}`)
        )
      );

      if (attempts >= 20) continue;

      const radius = 5 + Math.random() * 3;

      // Clear the area
      for (let y = cy - radius; y < cy + radius; y++) {
        for (let x = cx - radius; x < cx + radius; x++) {
          const ddx = x - cx;
          const ddy = y - cy;
          const dist = Math.sqrt(ddx * ddx + ddy * ddy);
          if (dist < radius) {
            const ix = Math.floor(x);
            const iy = Math.floor(y);
            if (ix >= 0 && ix < this.width && iy >= 0 && iy < this.height) {
              if (!this.riverTiles.has(`${ix},${iy}`)) {
                this.tiles[iy][ix] = TileType.DIRT;
              }
            }
          }
        }
      }

      // Add structures based on biome
      this.addClearingStructures(Math.floor(cx), Math.floor(cy));
    }
  }

  private addClearingStructures(cx: number, cy: number): void {
    switch (this.biome) {
      case 'forest':
      case 'sacred':
        // Small camp or house
        this.features.push({ type: 'house', x: cx, y: cy });
        this.features.push({ type: 'campfire', x: cx + 3, y: cy + 2 });
        this.features.push({ type: 'barrel', x: cx - 2, y: cy + 1 });
        this.features.push({ type: 'cart', x: cx + 4, y: cy - 2 });
        break;

      case 'swamp':
        // Abandoned camp
        this.features.push({ type: 'tent', x: cx, y: cy });
        this.features.push({ type: 'campfire', x: cx + 2, y: cy + 2 });
        this.features.push({ type: 'crate', x: cx - 2, y: cy });
        break;

      case 'desert':
        // Oasis or ruins
        this.features.push({ type: 'tent', x: cx, y: cy });
        this.features.push({ type: 'well', x: cx + 3, y: cy });
        this.features.push({ type: 'barrel', x: cx - 2, y: cy + 1 });
        break;

      case 'mountains':
        // Mining camp
        this.features.push({ type: 'tent', x: cx, y: cy });
        this.features.push({ type: 'crate', x: cx + 2, y: cy + 1 });
        this.features.push({ type: 'crate', x: cx + 3, y: cy + 1 });
        this.features.push({ type: 'torch', x: cx - 2, y: cy });
        break;

      case 'underworld':
      case 'hell':
        // Dark shrine
        this.features.push({ type: 'ruins', x: cx, y: cy });
        this.features.push({ type: 'torch', x: cx - 3, y: cy });
        this.features.push({ type: 'torch', x: cx + 3, y: cy });
        this.features.push({ type: 'grave', x: cx, y: cy + 3 });
        break;
    }
  }

  private generateBorder(): void {
    const borderWidth = 4;

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const distFromEdge = Math.min(x, y, this.width - 1 - x, this.height - 1 - y);

        if (distFromEdge < borderWidth) {
          const noise = this.noise.noise2D(x * 0.2, y * 0.2);
          const threshold = borderWidth - distFromEdge + noise * 2;

          if (threshold > 2) {
            // Impassable border
            if (this.biome === 'mountains') {
              this.tiles[y][x] = TileType.MOUNTAIN;
            } else if (this.biome === 'hell' || this.biome === 'underworld') {
              this.tiles[y][x] = TileType.LAVA;
            } else {
              this.tiles[y][x] = TileType.WATER;
            }
            this.riverTiles.add(`${x},${y}`);
          } else if (threshold > 1) {
            // Add border trees/rocks
            if (Math.random() < 0.1 && !this.riverTiles.has(`${x},${y}`)) {
              this.features.push({
                type: this.biome === 'desert' || this.biome === 'mountains' ? 'rock' : 'tree',
                x, y,
                variant: Math.floor(Math.random() * 3)
              });
            }
          }
        }
      }
    }
  }

  private generateDecorations(): void {
    // Scatter some decorations across the map
    const decorCount = Math.floor(this.width * this.height * 0.008);

    for (let i = 0; i < decorCount; i++) {
      const x = Math.floor(Math.random() * this.width);
      const y = Math.floor(Math.random() * this.height);

      // Skip water, paths, and existing features
      if (this.riverTiles.has(`${x},${y}`)) continue;
      if (this.pathTiles.has(`${x},${y}`)) continue;

      // Skip near center
      const dcx = x - this.width / 2;
      const dcy = y - this.height / 2;
      const distToCenter = Math.sqrt(dcx * dcx + dcy * dcy);
      if (distToCenter < 8) continue;

      // Add decoration based on biome
      const type = this.getRandomDecoration();
      if (type) {
        this.features.push({ type, x, y, variant: Math.floor(Math.random() * 3) });
      }
    }
  }

  private getRandomDecoration(): MapFeature['type'] | null {
    const roll = Math.random();

    switch (this.biome) {
      case 'forest':
        if (roll < 0.4) return 'tree';
        if (roll < 0.6) return 'bush';
        if (roll < 0.75) return 'rock';
        return null;

      case 'swamp':
        if (roll < 0.3) return 'tree';
        if (roll < 0.5) return 'bush';
        if (roll < 0.6) return 'rock';
        return null;

      case 'desert':
        if (roll < 0.5) return 'rock';
        if (roll < 0.6) return 'ruins';
        return null;

      case 'mountains':
        if (roll < 0.6) return 'rock';
        if (roll < 0.75) return 'tree';
        return null;

      case 'underworld':
        if (roll < 0.3) return 'rock';
        if (roll < 0.5) return 'grave';
        if (roll < 0.6) return 'torch';
        return null;

      case 'sacred':
        if (roll < 0.3) return 'tree';
        if (roll < 0.5) return 'bush';
        if (roll < 0.6) return 'statue';
        return null;

      case 'hell':
        if (roll < 0.4) return 'rock';
        if (roll < 0.6) return 'torch';
        return null;

      default:
        return null;
    }
  }
}
